import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import PropertyCard from '../components/common/PropertyCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Heart } from 'lucide-react';

const FavoritesPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    
    const fetchFavorites = async () => {
      try {
        setLoading(true);
        const data = await apiService.getFavorites();
        // Map the API response to the Property format
        const mapped = (data.items || []).map((l: any) => ({
          id: l.id,
          title: l.title,
          description: l.description ?? '',
          price: l.price ?? 0,
          address: {
            street: l.address ?? '',
            city: l.city ?? '',
            state: '',
            zipCode: l.zip_code ?? l.zipCode ?? '',
            country: l.country ?? ''
          },
          images: (l.images ?? []).map((img: any) => img.public_url ?? img.publicUrl).filter(Boolean),
          type: 'apartment',
          status: (l.purpose === 'RENT' ? 'rent' : 'sale'),
          features: {
            bedrooms: l.rooms ?? 0,
            bathrooms: l.bathrooms ?? 0,
            area: Number(l.area_m2 ?? l.areaM2 ?? 0),
            garage: false,
            yearBuilt: new Date(l.created_at ?? l.createdAt ?? new Date()).getFullYear()
          },
          amenities: [],
          contactInfo: { name: '', phone: '', email: '' },
          owner: l.owner_user_id ?? l.ownerUserId ?? '',
          createdAt: l.created_at ?? l.createdAt ?? new Date().toISOString(),
          updatedAt: l.updated_at ?? l.updatedAt ?? new Date().toISOString()
        }));
        setFavorites(mapped);
      } catch (e: any) {
        setError(e?.message || t('home.errors.failedToLoadListings'));
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [user, t]);

  const handlePropertyClick = (id: string) => {
    navigate(`/property/${id}`);
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // If not authenticated and not loading, the useEffect will redirect
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <Heart size={32} className="text-red-500 mr-3 fill-current" />
            <h1 className="text-3xl font-bold text-gray-900">
              {t('profile.favorites')}
            </h1>
          </div>
          <p className="text-gray-600">
            {t('profile.favoritesDescription')}
          </p>
        </div>
        
        {loading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}
        
        {error && <p className="text-red-600">{error}</p>}
        
        {!loading && !error && favorites.length === 0 ? (
          <div className="text-center py-12">
            <Heart size={64} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4 text-lg">
              {t('profile.noFavorites') || 'You have no favorite properties yet'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              {t('common.browseProperties')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {favorites.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onClick={() => handlePropertyClick(property.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
